import React, { useCallback, useRef, useState } from 'react';
import { Col, ListGroup, Row } from 'react-bootstrap';
// eslint-disable-next-line no-restricted-imports -- v5 form stack, migrate to v7 (DX-194)
import { FormContext } from 'react-hook-form-legacy';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/button';
import { ControlledInput } from '../../../../components/form/Input';
import SubmitButton from '../../../../components/form/SubmitButton';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import {
  TimeEntryNotesFormData,
  useTimeEntryNotesForm,
} from '../../hooks/useTimeEntryNotesForm';
import { useUpdateMyTimeEntryNotesMutation } from '../../__generated__/graphql';
import {
  ExtraFieldsAreaSimple,
  ExtraFieldsAreaSimpleRef,
} from '../ExtraFields/ExtraFieldsArea/ExtraFieldsAreaSimple';
import {
  ExtraFieldInternalValue,
  mapToExtraFieldInput,
} from '../ExtraFields/extraFieldInternalValue';
import {
  ExtraFieldsSupportedObject,
  ExtraFieldsFormId,
  ExtraFieldContextParam,
} from '../ExtraFields/types';

type Props = {
  timeEntryId: string;
  notes: string | null;
  extraFieldValues: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
  contextParams: ExtraFieldContextParam[];
};

//TODO: Test this case!
const NotesFormListGroupItem = React.forwardRef<HTMLFormElement, Props>(
  (
    { notes, timeEntryId, extraFieldValues, recordTypeName, contextParams },
    ref,
  ) => {
    const { t } = useTranslation();
    const { form, fieldName } = useTimeEntryNotesForm({ notes: notes });
    const [updateNotes] = useUpdateMyTimeEntryNotesMutation({
      update(cache, { data }) {
        if (!data?.updateMyTimeEntryNotes) return;
        // The mutation returns MyTimeEntry but the timesheet reads TimeEntry
        // (different __typename, same record). Evict the timeSheetDay root
        // field to force the timesheet query to refetch with fresh data.
        cache.evict({ fieldName: 'timeSheetDay' });
        cache.gc();
      },
    });
    const errorHandler = useMutationErrorHandler();

    const [extraFields, setExtraFields] =
      useState<readonly ExtraFieldInternalValue[]>(extraFieldValues);
    const [extraFieldsDirty, setExtraFieldsDirty] = useState(false);
    const extraFieldsRef = useRef<ExtraFieldsAreaSimpleRef>(null);

    const handleExtraFieldChange = useCallback(
      (values: ExtraFieldInternalValue[]) => {
        setExtraFields(values);
        setExtraFieldsDirty(true);
      },
      [],
    );

    const onSubmit = useCallback(
      async (formData: TimeEntryNotesFormData) => {
        const extraValid =
          (await extraFieldsRef.current?.triggerValidation()) ?? true;
        if (!extraValid) {
          return;
        }
        try {
          await updateNotes({
            variables: {
              input: {
                id: timeEntryId,
                notes: formData.notes || null,
                extraFields: extraFields.map(mapToExtraFieldInput),
              },
            },
          });
          setExtraFieldsDirty(false);
        } catch (e) {
          errorHandler(e);
        }
      },
      [timeEntryId, updateNotes, errorHandler, extraFields],
    );

    const onCancel = useCallback(() => {
      form.reset();
      setExtraFields(extraFieldValues);
      setExtraFieldsDirty(false);
    }, [form, extraFieldValues]);

    const showButtons = form.formState.dirty || extraFieldsDirty;

    return (
      <ListGroup.Item>
        <FormContext {...form}>
          <form ref={ref} onSubmit={form.handleSubmit(onSubmit)}>
            <Row>
              <Col>
                <ControlledInput
                  name={fieldName}
                  type="textarea"
                  minRows={4}
                  size="sm"
                />
              </Col>
            </Row>
            <div className="mt-3">
              <ExtraFieldsAreaSimple
                ref={extraFieldsRef}
                objectApiName={ExtraFieldsSupportedObject.TimeEntry}
                formId={ExtraFieldsFormId.TimeEntryEdit}
                onChange={handleExtraFieldChange}
                values={extraFields}
                recordTypeName={recordTypeName}
                contextParams={contextParams}
              />
            </div>
            {showButtons && (
              <Row className="mt-3">
                <Col className="d-flex justify-content-end">
                  <SubmitButton size="sm">
                    {t('form.general.saveButton')}
                  </SubmitButton>
                  <Button
                    type="reset"
                    className="ms-3"
                    size="sm"
                    label={t('form.general.cancelButton')}
                    variant="outline-secondary"
                    onClick={onCancel}
                  />
                </Col>
              </Row>
            )}
          </form>
        </FormContext>
      </ListGroup.Item>
    );
  },
);

export default NotesFormListGroupItem;
